import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Search,
  Send,
  MoreVertical,
  Bot,
  User,
  Loader2,
  AlertCircle,
  RefreshCw,
  Phone,
  Pause,
  Play,
  Wifi,
  WifiOff,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useConversations,
  useConversation,
  useUpdateConversationAi,
  useSendMessage,
} from "@/hooks/useConversations";
import { useLeads } from "@/hooks/useLeads";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { getFormattedLeadPhone } from "@/utils/leadUtils";
import { chatLogger, leadsLogger } from "@/utils/logger";
import { useWebSocket } from "@/contexts/WebSocketContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";

const Chat = () => {
  const [selectedConversation, setSelectedConversation] = useState<string>("");
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [messages, setMessages] = useState<any[]>([]);

  // Ref para scroll automático
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Integração com o backend usando WebSocket/polling
  const {
    conversations,
    isLoading: isLoadingConversations,
    error: conversationsError,
    refetch: refetchConversations,
  } = useConversations();

  const { data: selectedConversationData, isLoading: isLoadingConversation } =
    useConversation(selectedConversation);

  const { currentUser } = useCurrentUser();

  // Buscar dados dos leads para todas as conversas
  const { data: leadsData = [], isLoading: isLoadingLeads } = useLeads();

  // Criar mapa de leads para acesso rápido
  const leadsMap = useMemo(() => {
    const map = new Map();
    leadsData.forEach((lead) => {
      map.set(lead.id, lead);
    });

    // Debug: verificar dados dos leads
    if (leadsData.length > 0) {
      leadsLogger.log("PRIMEIRO LEAD COMPLETO:", leadsData[0]);
      leadsLogger.log("CAMPOS DO LEAD:", Object.keys(leadsData[0]));
      leadsLogger.log("waId:", leadsData[0].waId);
      leadsLogger.log("phone:", leadsData[0].phone);
    }

    return map;
  }, [leadsData]);

  // Com flex-col-reverse, não precisamos mais de scroll automático
  // As mensagens já aparecem naturalmente no bottom

  const updateAiMutation = useUpdateConversationAi();
  const sendMessageMutation = useSendMessage();

  const ws = useWebSocket();

  // Assinar/desassinar tópico global da empresa
  useEffect(() => {
    chatLogger.log("[DEBUG] currentUser.schemaName:", currentUser?.schemaName);
    chatLogger.log("[DEBUG] ws.status:", ws.status);
    const topic = `/topic/company.${currentUser?.schemaName}`;
    chatLogger.log("[DEBUG] Subscribing to topic:", topic);
    const handler = (msg: any) => {
      chatLogger.log("🟢 Mensagem recebida via WebSocket:", msg);
      setMessages((prev: any[]) => {
        const updated = [...prev, msg];
        // Ordenar do mais antigo para o mais novo
        return updated.sort((a, b) => {
          const tA = parseInt(a.timestamp);
          const tB = parseInt(b.timestamp);
          return tA - tB;
        });
      });
    };
    const unsubscribe = ws.subscribe(topic, handler);
    return unsubscribe;
  }, [currentUser?.schemaName, ws]);

  // 1. Carregar histórico ao selecionar conversa
  useEffect(() => {
    if (selectedConversationData?.messages) {
      setMessages(
        [...selectedConversationData.messages].sort((a, b) => {
          const tA = parseInt(a.timestamp);
          const tB = parseInt(b.timestamp);
          return tA - tB;
        })
      );
    } else {
      setMessages([]);
    }
  }, [selectedConversationData]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
    }
  }, [messages]);

  const filteredConversations = conversations
    .filter((conv) =>
      conv.name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Ordenar por timestamp da última mensagem (mais recente primeiro)
      const timestampA = a.lastMessageTimestamp
        ? Number(a.lastMessageTimestamp)
        : 0;
      const timestampB = b.lastMessageTimestamp
        ? Number(b.lastMessageTimestamp)
        : 0;

      // Se ambos têm timestamp, ordenar por timestamp (decrescente)
      if (timestampA && timestampB) {
        return timestampB - timestampA;
      }

      // Se apenas um tem timestamp, ele vem primeiro
      if (timestampA && !timestampB) return -1;
      if (!timestampA && timestampB) return 1;

      // Fallback: se nenhum tem timestamp, verificar se tem mensagem
      const hasMessageA = a.lastMessage && a.lastMessage.trim().length > 0;
      const hasMessageB = b.lastMessage && b.lastMessage.trim().length > 0;

      if (hasMessageA && !hasMessageB) return -1;
      if (!hasMessageA && hasMessageB) return 1;

      // Por último, ordenar por nome
      return (a.name || "").localeCompare(b.name || "");
    });

  // Debug: mostrar a ordenação das conversas
  chatLogger.group("Ordenação das Conversas");
  filteredConversations.slice(0, 5).forEach((conv, index) => {
    const timestamp = conv.lastMessageTimestamp
      ? Number(conv.lastMessageTimestamp)
      : 0;
    const date = timestamp
      ? new Date(
          timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp
        ).toLocaleString()
      : "N/A";
    chatLogger.log(`${index + 1}. ${conv.name} - Última mensagem: ${date}`);
  });
  chatLogger.groupEnd();

  // Separar conversas por status do useAi (já ordenadas por última mensagem)
  const manualConversations = filteredConversations.filter(
    (conv) => !conv.useai // useAi=false significa controle manual
  );
  const autoConversations = filteredConversations.filter(
    (conv) => conv.useai // useAi=true significa IA automática
  );

  const selectedConv = conversations.find((c) => c.id === selectedConversation);
  const selectedLead = selectedConv ? leadsMap.get(selectedConv.leadid) : null;
  const currentManualControl = selectedConv ? !selectedConv.useai : false; // Invertido

  // Função para ordenar mensagens por timestamp
  const getSortedMessages = () => {
    if (!selectedConversationData?.messages) return [];

    return [...selectedConversationData.messages].sort((a, b) => {
      // Converter BigInteger timestamp para número para comparação
      const timestampA = parseInt(a.timestamp);
      const timestampB = parseInt(b.timestamp);

      // Ordenar do mais antigo para o mais recente (ordem cronológica)
      return timestampA - timestampB;
    });
  };

  // Função para determinar se a mensagem é do lead ou da IA/sistema
  const isLeadMessage = (origin: string) => {
    // Considerar como mensagem do lead se:
    // - origin não é "IA", "AI", "Sistema", "System", "Bot", "Assistant"
    // - origin não está vazio
    // - origin não é igual ao nome da conversa (evitar confusão)
    const systemOrigins = [
      "ia",
      "ai",
      "sistema",
      "system",
      "bot",
      "assistant",
      "você",
      "voce",
    ];
    const lowerOrigin = origin.toLowerCase().trim();

    return !systemOrigins.includes(lowerOrigin) && origin.trim() !== "";
  };

  // Função para formatar número de telefone
  const formatPhoneNumber = (phone: string) => {
    if (!phone) return "Telefone não informado";

    // Remove caracteres não numéricos
    const cleanPhone = phone.replace(/\D/g, "");

    // Formatar telefone brasileiro
    if (cleanPhone.length === 11) {
      // Celular: (11) 99999-9999
      return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(
        2,
        7
      )}-${cleanPhone.slice(7)}`;
    } else if (cleanPhone.length === 10) {
      // Fixo: (11) 9999-9999
      return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(
        2,
        6
      )}-${cleanPhone.slice(6)}`;
    } else if (cleanPhone.length === 13 && cleanPhone.startsWith("55")) {
      // Com código do país: +55 (11) 99999-9999
      const withoutCountry = cleanPhone.slice(2);
      return `+55 (${withoutCountry.slice(0, 2)}) ${withoutCountry.slice(
        2,
        7
      )}-${withoutCountry.slice(7)}`;
    }

    // Se não conseguir formatar, retorna o número original
    return phone;
  };

  // Função para obter dados do lead de uma conversa
  const getLeadData = (conversation: any) => {
    return leadsMap.get(conversation.leadid);
  };

  // Enviar mensagem usando o contexto global
  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    sendMessageMutation.mutate(
      { conversationId: selectedConversation, message: newMessage },
      { onSuccess: () => setNewMessage("") }
    );
  };

  const toggleConversationControl = (conversationId: string) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    if (conversation) {
      // Se estiver tentando mudar para modo manual (useai = false) e não tiver atividade recente, bloquear
      if (conversation.useai && !isRecentActivity(conversation)) {
        toast.error(
          "Não é possível ativar o modo manual em conversas inativas",
          {
            description: "A conversa não teve atividade nas últimas 24 horas",
          }
        );
        return;
      }

      // Inverter o valor atual de useAi
      updateAiMutation.mutate({
        id: conversationId,
        useai: !conversation.useai,
      });
    }
  };

  // Handler para input de mensagem (simplificado)
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);
    // Indicadores de digitação serão implementados futuramente
  };

  // Handler para quando para de digitar (simplificado)
  const handleMessageBlur = () => {
    // Indicadores de digitação serão implementados futuramente
  };

  const getInitials = (name: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  /**
   * Verifica se a conversa teve atividade recente (menos de 24 horas)
   *
   * A bolinha de status fica:
   * - Verde: se a última mensagem foi enviada há menos de 24 horas
   * - Vermelha: se a última mensagem foi enviada há mais de 24 horas ou não há mensagens
   *
   * @param conversation Objeto da conversa
   * @returns true se a conversa teve atividade recente, false caso contrário
   */
  /**
   * Verifica se a conversa teve atividade recente (menos de 24 horas)
   *
   * A bolinha de status fica:
   * - Verde: se a última mensagem foi enviada há menos de 24 horas
   * - Vermelha: se a última mensagem foi enviada há mais de 24 horas ou não há mensagens
   *
   * @param conversation Objeto da conversa
   * @returns true se a conversa teve atividade recente, false caso contrário
   */
  const isRecentActivity = (conversation: any) => {
    // Se não tiver timestamp da última mensagem
    if (!conversation.lastMessageTimestamp) {
      chatLogger.log(
        `Conversa ${conversation.id} sem timestamp da última mensagem`
      );
      return false;
    }

    // O backend envia o timestamp como string, precisamos converter para número
    // O BigInteger do Java é serializado como string no JSON
    const lastMessageTime =
      typeof conversation.lastMessageTimestamp === "string"
        ? parseInt(conversation.lastMessageTimestamp)
        : Number(conversation.lastMessageTimestamp);

    if (isNaN(lastMessageTime)) {
      chatLogger.warn(
        `Conversa ${conversation.id} com timestamp inválido: ${conversation.lastMessageTimestamp}`
      );
      return false;
    }

    const now = Date.now();

    // Converter para milissegundos se necessário
    // O backend pode enviar timestamp em segundos (10 dígitos) ou milissegundos (13 dígitos)
    const messageTimeMs =
      lastMessageTime.toString().length <= 10
        ? lastMessageTime * 1000 // Se for em segundos
        : lastMessageTime; // Se já for em milissegundos

    const hoursDiff = (now - messageTimeMs) / (1000 * 60 * 60);

    const date = new Date(messageTimeMs);
    chatLogger.log(
      `Conversa ${
        conversation.id
      }: última mensagem em ${date.toLocaleString()}, há ${hoursDiff.toFixed(
        2
      )} horas`
    );

    // Retorna true se a última mensagem foi enviada há menos de 24 horas
    return hoursDiff < 24;
  };

  // Função para formatação do timestamp
  const formatTimestamp = (timestamp: string) => {
    try {
      // Converter BigInteger timestamp para número
      const timestampNumber = parseInt(timestamp);

      // Verificar se o timestamp é válido
      if (isNaN(timestampNumber) || timestampNumber <= 0) {
        chatLogger.warn("Timestamp inválido:", timestamp);
        return "Horário inválido";
      }

      // Criar objeto Date a partir do timestamp
      // Se o timestamp estiver em segundos, multiplicar por 1000
      // Se já estiver em milissegundos, usar diretamente
      let date;

      // Verificar se o timestamp está em segundos ou milissegundos
      // Timestamps em milissegundos são muito maiores (13+ dígitos)
      // Timestamps em segundos são menores (10 dígitos)
      if (timestampNumber.toString().length <= 10) {
        // Timestamp em segundos - converter para milissegundos
        date = new Date(timestampNumber * 1000);
      } else {
        // Timestamp já em milissegundos
        date = new Date(timestampNumber);
      }

      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        chatLogger.warn(
          "Data inválida após conversão:",
          timestamp,
          "→",
          timestampNumber
        );
        return "Horário inválido";
      }

      // Verificar se a data não é muito antiga ou muito futura (sanity check)
      const now = new Date();
      const oneYearAgo = new Date(
        now.getFullYear() - 1,
        now.getMonth(),
        now.getDate()
      );
      const oneYearFromNow = new Date(
        now.getFullYear() + 1,
        now.getMonth(),
        now.getDate()
      );

      if (date < oneYearAgo || date > oneYearFromNow) {
        chatLogger.warn("Data fora do intervalo esperado:", date.toISOString());
        // Ainda assim, mostrar a data formatada
      }

      // Formatação completa com data e hora
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      // Se for hoje, mostrar apenas a hora
      if (date.toDateString() === today.toDateString()) {
        return date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        });
      }

      // Se for ontem, mostrar "Ontem HH:MM"
      if (date.toDateString() === yesterday.toDateString()) {
        return `Ontem ${date.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      }

      // Se for mais antigo, mostrar data completa
      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      chatLogger.error("Erro ao formatar timestamp:", timestamp, error);
      return "Horário inválido";
    }
  };

  const handleRetry = () => {
    refetchConversations();
  };

  const renderConversationItem = (conversation: any) => {
    const isManualControlEnabled = !conversation.useai; // Invertido
    const leadData = getLeadData(conversation);

    return (
      <div
        key={conversation.id}
        className={cn(
          "p-4 border-b cursor-pointer hover:bg-accent transition-colors",
          selectedConversation === conversation.id &&
            "bg-accent border-l-4 border-l-primary",
          isManualControlEnabled && "bg-primary/5 border-l-4 border-l-primary"
        )}
      >
        <div
          className="flex items-center gap-3 mb-3"
          onClick={() => setSelectedConversation(conversation.id)}
        >
          <div className="relative">
            <Avatar>
              <AvatarFallback>{getInitials(conversation.name)}</AvatarFallback>
            </Avatar>
            <div
              className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-background ${
                isRecentActivity(conversation) ? "bg-green-500" : "bg-red-500"
              }`}
              title={
                isRecentActivity(conversation)
                  ? "Atividade recente"
                  : "Sem atividade recente (>24h)"
              }
            />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-foreground truncate">
                {conversation.name || `Conversa ${conversation.id.slice(0, 8)}`}
              </h3>
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm text-muted-foreground truncate">
                {conversation.lastMessage || "Nenhuma mensagem"}
              </p>
              {conversation.lastMessageTimestamp && (
                <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
                  {formatTimestamp(
                    conversation.lastMessageTimestamp.toString()
                  )}
                </span>
              )}
            </div>
            {/* TELEFONE DO LEAD */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              <Phone className="h-3 w-3" />
              <span>
                {isLoadingLeads
                  ? "Carregando..."
                  : leadData
                  ? getFormattedLeadPhone(leadData)
                  : "Telefone não informado"}
              </span>
            </div>
          </div>
        </div>

        {/* Toggle de Controle Manual por conversa */}
        <div
          className={cn(
            "flex flex-col p-2 rounded-lg border transition-colors",
            isManualControlEnabled
              ? "bg-primary/10 border-primary/30"
              : "bg-muted/50 border-muted"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              {isManualControlEnabled ? (
                <User className="h-3 w-3 text-primary" />
              ) : (
                <Bot className="h-3 w-3 text-muted-foreground" />
              )}
              <Label
                htmlFor={`manual-control-${conversation.id}`}
                className={cn(
                  "text-xs font-medium",
                  !isRecentActivity(conversation) && !isManualControlEnabled
                    ? "cursor-not-allowed opacity-70"
                    : "cursor-pointer"
                )}
                title={
                  !isRecentActivity(conversation) && !isManualControlEnabled
                    ? "Não é possível ativar o modo manual em conversas inativas"
                    : ""
                }
              >
                {isManualControlEnabled ? "Manual" : "IA Auto"}
              </Label>
            </div>
            <Switch
              id={`manual-control-${conversation.id}`}
              checked={isManualControlEnabled}
              onCheckedChange={() => toggleConversationControl(conversation.id)}
              className="scale-75"
              disabled={
                updateAiMutation.isPending ||
                (!isManualControlEnabled && !isRecentActivity(conversation))
              }
              title={
                !isRecentActivity(conversation) && !isManualControlEnabled
                  ? "Não é possível ativar o modo manual em conversas inativas"
                  : ""
              }
            />
          </div>
          {/* Mensagem de ajuda para conversas inativas */}
          {!isRecentActivity(conversation) && !isManualControlEnabled && (
            <p className="text-xs text-red-500 mt-1">
              Conversa inativa há mais de 24h
            </p>
          )}
        </div>
      </div>
    );
  };

  // Renderizar erro específico para 500
  if (conversationsError) {
    const isServerError = (conversationsError as any)?.response?.status === 500;

    return (
      <div className="p-6 space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {isServerError ? (
              <div className="space-y-2">
                <p>
                  <strong>Erro interno do servidor (500)</strong>
                </p>
                <p>Há um problema no backend ao buscar as conversas.</p>
                <p className="text-sm">Possíveis causas:</p>
                <ul className="text-sm list-disc list-inside ml-4">
                  <li>Banco de dados não está rodando</li>
                  <li>Tabela 'conversations' não existe ou está vazia</li>
                  <li>Problema de mapeamento de entidades</li>
                  <li>Erro de relacionamento entre tabelas</li>
                </ul>
              </div>
            ) : (
              `Erro ao carregar conversas: ${
                (conversationsError as any)?.message || "Erro desconhecido"
              }`
            )}
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Button onClick={handleRetry} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex gap-4">
      {/* Lista de Conversas */}
      <Card className="w-1/3">
        <CardContent className="p-0 h-full flex flex-col">
          <div className="p-4 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Conversas</h2>
              <Button onClick={handleRetry} variant="ghost" size="sm">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar conversas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            {isLoadingConversations ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Carregando conversas...</span>
              </div>
            ) : (
              <Tabs defaultValue="auto" className="h-full flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mx-4 mt-2">
                  <TabsTrigger value="auto" className="flex items-center gap-2">
                    <Bot className="h-4 w-4" />
                    IA Auto
                    <Badge variant="secondary" className="text-xs ml-1">
                      {autoConversations.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="manual"
                    className="flex items-center gap-2"
                  >
                    <User className="h-4 w-4" />
                    Manual
                    <Badge variant="secondary" className="text-xs ml-1">
                      {manualConversations.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>

                <TabsContent
                  value="manual"
                  className="flex-1 overflow-y-auto mt-0"
                >
                  {manualConversations.length > 0 ? (
                    manualConversations.map(renderConversationItem)
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma conversa em modo manual</p>
                      <p className="text-xs mt-1">
                        Ative o controle manual em uma conversa para vê-la aqui
                      </p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent
                  value="auto"
                  className="flex-1 overflow-y-auto mt-0"
                >
                  {autoConversations.length > 0 ? (
                    autoConversations.map(renderConversationItem)
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      <Bot className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma conversa em modo IA</p>
                      <p className="text-xs mt-1">
                        Conversas com IA automática aparecerão aqui
                      </p>
                    </div>
                  )}
                </TabsContent>

                {/* Mensagem quando não há conversas */}
                {filteredConversations.length === 0 && (
                  <div className="p-4 text-center text-muted-foreground">
                    <p>Nenhuma conversa encontrada</p>
                    <Button
                      onClick={handleRetry}
                      variant="outline"
                      size="sm"
                      className="mt-2"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Recarregar
                    </Button>
                  </div>
                )}
              </Tabs>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Área de Chat */}
      <Card className="flex-1">
        <CardContent className="p-0 h-full flex flex-col">
          {/* Indicador de status de conexão WebSocket */}
          <div className="flex items-center gap-2 mb-2 p-2">
            {ws.status === "connected" && (
              <span className="text-green-600">
                Conectado ao chat em tempo real
              </span>
            )}
            {ws.status === "connecting" && (
              <span className="text-yellow-600">Conectando ao chat...</span>
            )}
            {ws.status === "reconnecting" && (
              <span className="text-yellow-600">Reconectando ao chat...</span>
            )}
            {ws.status === "disconnected" && (
              <span className="text-red-600">Desconectado do chat</span>
            )}
          </div>
          {selectedConversation ? (
            <>
              {/* Header da Conversa - COM DADOS DO LEAD E CONTROLES DE POLLING */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar>
                    <AvatarFallback>
                      {selectedConv ? getInitials(selectedConv.name) : "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-foreground">
                      {selectedConv?.name ||
                        `Conversa ${selectedConversation.slice(0, 8)}`}
                    </h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      <span>
                        {isLoadingLeads
                          ? "Carregando..."
                          : selectedLead
                          ? getFormattedLeadPhone(selectedLead)
                          : "Telefone não informado"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Indicador de Status SSE */}
                  <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-muted/50">
                    <Wifi className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600">Tempo Real</span>
                  </div>

                  {/* Toggle SSE */}
                  <Button
                    onClick={() => {}}
                    variant="ghost"
                    size="sm"
                    title="Modo tempo real ativado"
                  >
                    <Wifi className="h-4 w-4" />
                  </Button>

                  {/* Refresh manual */}
                  <Button
                    onClick={() => refetchConversations()}
                    variant="ghost"
                    size="sm"
                    title="Atualizar mensagens"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>

                  <Button variant="ghost" size="sm">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Mensagens - LAYOUT LIMPO SEM AVATARES E NOMES */}
              <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
                {isLoadingConversation ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="ml-2">Carregando mensagens...</span>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isFromLead = isLeadMessage(message.origin);
                    return (
                      <div
                        key={message.id}
                        className={cn(
                          "flex",
                          isFromLead ? "justify-start" : "justify-end"
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-xs lg:max-w-md px-4 py-2 rounded-lg",
                            isFromLead
                              ? "bg-muted text-foreground"
                              : "bg-primary text-primary-foreground"
                          )}
                        >
                          <p className="text-sm">{message.text}</p>
                          <p
                            className={cn(
                              "text-xs mt-1",
                              isFromLead
                                ? "text-muted-foreground"
                                : "text-primary-foreground/70"
                            )}
                          >
                            {formatTimestamp(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />

                {/* Mensagem quando não há mensagens */}
                {messages.length === 0 && !isLoadingConversation && (
                  <div className="text-center text-muted-foreground py-8">
                    <p>Nenhuma mensagem nesta conversa</p>
                  </div>
                )}
              </div>

              {/* Input de Nova Mensagem */}
              <div className="p-4 border-t">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder={
                      currentManualControl
                        ? "Digite sua mensagem..."
                        : selectedConv && !isRecentActivity(selectedConv)
                        ? "Conversa inativa há mais de 24h - não é possível ativar modo manual"
                        : "Controle manual desabilitado - IA respondendo automaticamente"
                    }
                    value={newMessage}
                    onChange={handleMessageChange}
                    onBlur={handleMessageBlur}
                    onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                    className="flex-1"
                    disabled={!currentManualControl}
                  />
                  <Button
                    onClick={handleSendMessage}
                    size="sm"
                    disabled={
                      !currentManualControl || sendMessageMutation.isPending
                    }
                  >
                    {sendMessageMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {!currentManualControl && (
                  <p className="text-xs text-muted-foreground mt-2">
                    {selectedConv && !isRecentActivity(selectedConv) ? (
                      <span className="text-red-500">
                        Conversa inativa há mais de 24h. Não é possível ativar o
                        modo manual.
                      </span>
                    ) : (
                      "A IA está respondendo automaticamente. Ative o controle manual para enviar mensagens."
                    )}
                  </p>
                )}

                {/* Status de conexão */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1 text-green-600">
                      <Wifi className="h-3 w-3" />
                      Tempo real ativo ({messages.length} mensagens)
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="font-medium mb-2">Selecione uma conversa</h3>
                <p className="text-sm">
                  Escolha uma conversa da lista para visualizar as mensagens
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Chat;
